import { Action, Priority, useRegisterActions } from "kbar";
import { useCallback, useMemo } from "react";

import {
  ConnectionsWithUsageSummaryQuery,
  useConnectionsWithUsageSummaryLazyQuery,
} from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { IntegrationLogo, useIntegrationsMap } from "@/integrations";
import { useNavigateWithSlug } from "@/router";

type Connector = NonNullable<
  ConnectionsWithUsageSummaryQuery["connectors"]
>[number];

const formatSubtitle = (sync: Connector) => {
  if (sync.usageSummary.totalSyncs === 0) {
    return "No syncs yet";
  }
  const resultArray: string[] = [];

  const syncsOrSync = (n: number) => {
    return n === 1 ? "sync" : "syncs";
  };

  if (sync.usageSummary.numEltSyncs > 0) {
    resultArray.push(
      `${sync.usageSummary.numEltSyncs} Data Source ${syncsOrSync(sync.usageSummary.numEltSyncs)}`,
    );
  }
  if (sync.usageSummary.numRevEltSyncs > 0) {
    resultArray.push(
      `${sync.usageSummary.numRevEltSyncs} Reverse ETL ${syncsOrSync(sync.usageSummary.numRevEltSyncs)}`,
    );
  }
  return resultArray.join(" • ");
};

export function useConnectionActions() {
  const [fetchConnections, { data, loading }] =
    useConnectionsWithUsageSummaryLazyQuery();

  const navigate = useNavigateWithSlug();
  const integrationsMap = useIntegrationsMap();

  const actions: Action[] = useMemo(() => {
    const actions: Action[] = [];

    if (loading) {
      actions.push({
        id: "loading-connections",
        name: "Loading ...",
        priority: Priority.HIGH,
        section: "Connectors",
        icon: <LoadingSpinner id="loading" className="size-4" />,
        perform: () => {},
      });
    }

    data?.connectors.forEach((connection) => {
      const integration = integrationsMap.get(connection.integrationId);
      actions.push({
        id: connection.id,
        name: connection.label,
        subtitle: formatSubtitle(connection),
        keywords: integration
          ? [integration.name.toLowerCase()].join(" ")
          : undefined,
        priority: Priority.NORMAL,
        section: "Connectors",
        icon: (
          <IntegrationLogo id={connection.integrationId} className="size-4" />
        ),
        perform: () => {
          navigate({
            to: `/settings/connections/${connection.id}`,
          });
        },
      });
    });

    return actions;
  }, [data, loading, integrationsMap, navigate]);

  useRegisterActions(actions, [data?.connectors, loading]);

  const register = useCallback(async () => {
    await fetchConnections();
  }, [fetchConnections]);

  return register;
}
